<template>
  <div class="view-container survey-passings-page">
    <div class="extended-filter-block">
      <h2>Опросы</h2>
    </div>

    <div class="card card__with-action">
      <div class="filter-block">
        <a-select
            v-model:value="typeFilter"
            class="type-filter-select"
            placeholder="Тип опроса"
            allow-clear
            :options="surveyTypes"
            :not-found-content="'Нет данных'"
            @change="handleFilterChange"
        />

        <a-input-search
            v-model:value="userIdFilter"
            class="user-id-search"
            placeholder="user_id"
            allow-clear
            @search="handleFilterChange"
            @change="handleUserIdClear"
        />
      </div>

      <TableHorizontalScrollShell ref="tableShell">
        <a-table
            ref="tableRef"
            rowKey="code"
            class="survey-passings-table"
            :columns="columns"
            :data-source="passingsData"
            :scroll="tableScroll"
            :pagination="false"
            :loading="tableLoading"
            :table-layout="'fixed'"
            @change="handleTableChange"
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'user_id'">
              <a
                  v-if="text"
                  class="table-link"
                  @click="router.push(`/staff/${text}/edit`)"
              >
                {{ text }}
              </a>
            </template>

            <template v-else-if="column.dataIndex === 'code'">
              <a
                  class="table-link"
                  @click="openPassing(record.code)"
              >
                {{ text }}
              </a>
            </template>

            <template v-else-if="column.dataIndex === 'type'">
              <span>{{ getTypeLabel(text) }}</span>
            </template>

            <template v-else>
              {{ text }}
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img
                  :src="NotFoundIcon"
                  class="no-data-image"
                  alt="Нет результатов"
              />

              <div class="no-data-text">
                {{ hasFilters ? 'Нет данных для отображения. Измените фильтры или запрос.' : 'Нет данных для отображения.' }}
              </div>

              <a-button
                  v-if="hasFilters"
                  @click="resetFilters"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>

                Сбросить фильтры
              </a-button>
            </div>
          </template>
        </a-table>
      </TableHorizontalScrollShell>

      <div class="bottom-container">
        <a-pagination
            v-if="passingsPagination.last_page > 1"
            v-model:current="currentPage"
            :page-size="entitiesPerPage"
            :buildOptionText="count => `${count.value} / стр`"
            :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
            :total="totalPassings"
            :show-less-items="isMobile"
            @change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  computed,
  onBeforeMount,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { ReloadOutlined } from '@ant-design/icons-vue';

import NotFoundIcon from '@/assets/images/icons/no_data.svg';
import TableHorizontalScrollShell from '@/components/Tables/TableScrollComponent.vue';
import { useTableColumns } from '@/mixins/useTableColumns';
import useBreakpoints from '@/mixins/useBreakpoints';
import useCheckRights from '@/mixins/useCheckRights';

export default {
  name: 'survey-passings-page',

  components: {
    TableHorizontalScrollShell,
    ReloadOutlined,
  },

  setup() {
    const store = useStore();
    const router = useRouter();
    const { getRight } = useCheckRights();

    const surveyTypes = [
      { value: 'weekly', label: 'Еженедельный' },
      { value: 'monthly', label: 'Ежемесячный' },
      { value: 'quarterly', label: 'Ежеквартальный' },
    ];

    const tableLoading = ref(true);
    const entitiesPerPage = ref(10);
    const currentPage = ref(1);
    const typeFilter = ref(null);
    const userIdFilter = ref('');

    const sortState = ref({
      field: null,
      order: null,
    });

    const passings = computed(() => store.state.periodicSurveys.passings);
    const totalPassings = computed(() => store.state.periodicSurveys.passingsTotal);
    const passingsPagination = computed(() => store.state.periodicSurveys.passingsPagination);
    const listState = computed(() => store.state.periodicSurveys.passingsListState);
    const hasFilters = computed(() => Boolean(typeFilter.value || userIdFilter.value));

    const passingsData = computed(() => passings.value.map((passing) => ({
      code: passing.code,
      survey_id: passing.survey_id,
      created_at: passing.created_at ?? '',
      started_at: passing.started_at ?? '',
      finished_at: passing.finished_at ?? '',
      user_id: passing.user_id,
      type: passing.type ?? passing.survey?.period ?? '',
      status: passing.finished_at ? 'Завершен' : passing.started_at ? 'В процессе' : 'Создан',
    })));

    const initialColumns = ref([
      {
        title: 'Дата',
        dataIndex: 'finished_at',
        sorter: true,
        visible: true,
        width: 180,
      },
      {
        title: 'Тип',
        dataIndex: 'type',
        sorter: true,
        visible: true,
        width: 180,
      },
      {
        title: 'Код прохождения',
        dataIndex: 'code',
        sorter: true,
        visible: true,
        width: 220,
      },
      {
        title: 'user_id',
        dataIndex: 'user_id',
        sorter: true,
        visible: true,
        width: 140,
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        visible: true,
        width: 160,
      },
    ]);

    const {
      columns,
      scrollX,
      tableRef,
      tableScroll,
      updateTableYScroll,
    } = useTableColumns(initialColumns, {
      tableKey: 'survey_passings',
      settingsWidth: 40,
      dataSource: passingsData,
    });

    const getTypeLabel = (type) => {
      return surveyTypes.find((item) => item.value === type)?.label ?? type;
    };

    const openPassing = (code) => {
      const routeData = router.resolve(`/surveys/passings/${code}`);
      window.open(routeData.href, '_blank');
    };

    const fetchPassings = () => {
      tableLoading.value = true;

      store.dispatch('periodicSurveys/getPassings', {
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
        type: typeFilter.value,
        user_id: userIdFilter.value || null,
      }).then((response) => {
        if (response?.pagination) {
          currentPage.value = response.pagination.page ?? currentPage.value;
          entitiesPerPage.value = response.pagination.per_page ?? entitiesPerPage.value;
        }
      }).catch(() => null).finally(() => {
        tableLoading.value = false;
      });
    };

    const retrieveEntitiesPerPage = () => {
      const storageValue = localStorage.getItem('perPage');
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10;
    };

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num);
    };

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage;
        setEntitiesPerPage(entitiesPerPage.value);
      }

      currentPage.value = current;
      fetchPassings();
    };

    const handleTableChange = (pagination, filter, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      };

      fetchPassings();
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchPassings();
    };

    const handleUserIdClear = () => {
      if (!userIdFilter.value) {
        handleFilterChange();
      }
    };

    const resetFilters = () => {
      typeFilter.value = null;
      userIdFilter.value = '';
      currentPage.value = 1;
      fetchPassings();
    };

    onBeforeMount(() => {
      store.dispatch('profile/getProfile').then(() => {
        if (!getRight('superadmin')) {
          router.push('/');
          return;
        }

        currentPage.value = listState.value.currentPage ?? 1;
        typeFilter.value = listState.value.type ?? null;
        userIdFilter.value = listState.value.user_id ?? '';

        sortState.value = {
          field: listState.value.sortField ?? null,
          order: listState.value.sortOrder ?? null,
        };

        retrieveEntitiesPerPage();
        fetchPassings();
      });
    });

    const tableShell = ref(null);

    watch(tableLoading, async (loading) => {
      if (!loading) {
        await tableShell.value?.rebindScroll();
        await updateTableYScroll();
      }
    });

    watch(passingsData, async () => {
      await tableShell.value?.rebindScroll();
      await updateTableYScroll();
    }, {
      deep: true,
    });

    watch([
      currentPage,
      sortState,
      typeFilter,
      userIdFilter,
    ], () => {
      store.commit('periodicSurveys/setPassingsListState', {
        currentPage: currentPage.value,
        sortField: sortState.value.field,
        sortOrder: sortState.value.order,
        type: typeFilter.value,
        user_id: userIdFilter.value,
      });
    }, {
      deep: true,
    });

    onBeforeRouteLeave((to) => {
      const isStillInSurveyPassings = to.path.startsWith('/surveys/passings');

      if (!isStillInSurveyPassings) {
        store.dispatch('periodicSurveys/clearPassings');
        store.commit('periodicSurveys/clearPassingsListState');
      }
    });

    const { isMobile } = useBreakpoints(767);

    return {
      router,
      NotFoundIcon,
      columns,
      scrollX,
      tableRef,
      tableScroll,
      tableShell,
      tableLoading,
      passingsData,
      totalPassings,
      passingsPagination,
      entitiesPerPage,
      currentPage,
      surveyTypes,
      typeFilter,
      userIdFilter,
      hasFilters,
      isMobile,
      getTypeLabel,
      openPassing,
      handlePageChange,
      handleTableChange,
      handleFilterChange,
      handleUserIdClear,
      resetFilters,
    };
  },
};
</script>

<style scoped lang="scss">
.view-container {
  padding-top: 0;
  padding-bottom: 0;
}

.card__with-action {
  margin-bottom: 0;
}

.filter-block {
  display: flex;
  column-gap: 12px;
  row-gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.type-filter-select {
  width: 220px;
}

.user-id-search {
  width: 220px;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.table-link {
  color: var(--main-green);
  cursor: pointer;
}

.table-link:hover {
  color: var(--polar-green-3);
}

.no-data-wrapper {
  height: 50vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  padding: 24px;
  text-align: center;
}

.no-data-wrapper button {
  margin-top: 10px;
}

.no-data-wrapper button:hover {
  border-color: var(--main-green);
  color: var(--main-green);
}

.no-data-text {
  color: var(--main-font);
}

:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  padding: 5px;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr:not(.ant-table-measure-row) > td) {
  padding: 10px 5px !important;
}

:deep(.ant-input-affix-wrapper:hover),
:deep(.ant-input-affix-wrapper-focused),
:deep(.ant-select-focused .ant-select-selector) {
  border-color: var(--main-green) !important;
}

:deep(.ant-input-affix-wrapper-focused),
:deep(.ant-select-focused .ant-select-selector) {
  box-shadow: 0 0 0 2px var(--polar-green) !important;
}

@media (max-width: $mobile-breakpoint) {
  .type-filter-select,
  .user-id-search {
    width: 100%;
  }

  :deep(.ant-pagination .ant-pagination-total-text) {
    display: none !important;
  }
}
</style>
